import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

export interface SceneRig {
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  renderer: THREE.WebGLRenderer;
  controls: OrbitControls;
  stars: THREE.Points;
}

/**
 * Builds the base Three.js scene: renderer, camera, orbit controls (with
 * auto-rotate), ambient/point lighting, and a background starfield. Contains
 * no brain-specific or substance-specific logic.
 */
export function createSceneRig(container: HTMLElement): SceneRig {
  const scene = new THREE.Scene();

  const camera = new THREE.PerspectiveCamera(
    45,
    container.clientWidth / container.clientHeight,
    0.1,
    100,
  );
  // Weighted toward a lateral view: the profile that shows the temporal lobe,
  // the Sylvian fissure and the cerebellum tucked beneath the occipital lobe.
  // A more frontal angle hides the cerebellum behind the temporal lobe.
  camera.position.set(4.5, 0.85, 1.9);

  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
  renderer.setSize(container.clientWidth, container.clientHeight);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.2;
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  container.appendChild(renderer.domElement);

  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.dampingFactor = 0.08;
  controls.autoRotate = true;
  controls.autoRotateSpeed = 0.4;
  controls.minDistance = 3.5;
  controls.maxDistance = 11;
  controls.maxPolarAngle = Math.PI * 0.8;
  controls.target.set(0, -0.05, 0);
  controls.update();

  scene.add(new THREE.AmbientLight(0x1a3050, 1.5));

  const pointLight1 = new THREE.PointLight(0x00aacc, 30, 15, 0.5);
  pointLight1.position.set(5, 3, 5);
  scene.add(pointLight1);

  const pointLight2 = new THREE.PointLight(0x4422aa, 20, 12, 0.5);
  pointLight2.position.set(-4, -2, -3);
  scene.add(pointLight2);

  const stars = createStarfield();
  scene.add(stars);

  window.addEventListener('resize', () => onResize(container, camera, renderer));

  return { scene, camera, renderer, controls, stars };
}

function createStarfield(): THREE.Points {
  const starCount = 2000;
  const positions = new Float32Array(starCount * 3);
  for (let i = 0; i < starCount; i++) {
    positions[i * 3] = (Math.random() - 0.5) * 25;
    positions[i * 3 + 1] = (Math.random() - 0.5) * 18;
    positions[i * 3 + 2] = (Math.random() - 0.5) * 20 - 3;
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

  const material = new THREE.PointsMaterial({
    color: 0x335577,
    size: 0.015,
    transparent: true,
    opacity: 0.7,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });

  return new THREE.Points(geometry, material);
}

function onResize(container: HTMLElement, camera: THREE.PerspectiveCamera, renderer: THREE.WebGLRenderer): void {
  camera.aspect = container.clientWidth / container.clientHeight;
  camera.updateProjectionMatrix();
  renderer.setSize(container.clientWidth, container.clientHeight);
}
